import { useEffect, useState } from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import { FormHelperText, Stack, TextField } from '@mui/material'
import { Business as ManufacturerIcon } from '@mui/icons-material';

import { Section } from 'components/controls/section'
import { ImageFileInput } from 'components/controls/imageFileInput'
import { OptionSelect } from 'components/controls/optionSelect'
import { Loading }  from 'components/controls/loading'

import { ManufacturerFormData, ManufacturerResult, useGetManufacturerLogo } from "interface/manufacturer"
import { ClientResult, useListClient } from 'interface/carenet/client'

const {
  Required,
  TooLongLength,
  TooShortLength
} = validationMessages

const formSchema = yup.object().shape({
  manufacturerName: yup.string().required(Required)
    .min(2, TooShortLength(2))
    .max(200, TooLongLength(200)),
  manufacturerNameEng: yup.string()
    .max(200, TooLongLength(200)),
  description: yup.string(),
	client: yup.array().of(
		yup.number().required(Required)
	),
  updateTime: yup.string().nullable(),
})

type useManufacturerFormProps = {
	manufacturer?: ManufacturerResult
  onSubmit: (body: { form: ManufacturerFormData, logo?: File }) => void
}
export const useManufacturerForm = ({
	manufacturer,
	onSubmit
}: useManufacturerFormProps) => {
  const logoState = useGetManufacturerLogo(manufacturer?.manufacturerId, manufacturer?.updateTime)
  const [logoFile, setLogoFile] = useState<File | undefined>(undefined)
	const clientListState = useListClient()
	const form = useForm({
		defaultValues: manufacturer ? {
			manufacturerName: manufacturer.manufacturerName ?? '',
			manufacturerNameEng: manufacturer.manufacturerNameEng ?? '',
			description: manufacturer.description ?? '',
			client: manufacturer.client.map((c) => c.clientId),
			updateTime: manufacturer.updateTime
		} : {
			manufacturerName: '',
			manufacturerNameEng: '',
			description: '',
			client: []
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
		logo: {
			state: logoState,
			file: logoFile,
			set: setLogoFile
		},
		clientListState,
    submit: form.handleSubmit(input => onSubmit({
      form: {
				manufacturerName: input.manufacturerName,
				manufacturerNameEng: input.manufacturerNameEng,
				description: input.description,
				client: input.client,
				updateTime: manufacturer?.updateTime
      },
      logo: logoFile
		}))
	}
}

export const ManufacturerForm = ({
	control,
	formState: { errors },
	logo,
	clientListState
}: ReturnType<typeof useManufacturerForm>) => {
	const [clientList, setClientList] = useState<ClientResult[]>()
	useEffect(
		() => setClientList(clientListState.data?.list ?? []),
		[clientListState]
	)
	return (
		<Stack direction="row" spacing={2}>
			<Stack direction="column" spacing={1} width="200px" justifyContent="center">
				<ImageFileInput
					state={logo.state}
					onChangeFile={logo.set}
					emptyImage={<ManufacturerIcon fontSize='large'/>}
					alt="ロゴ画像"
				/>
			</Stack>

			<Stack direction="column" flexGrow={1}>
				<Section label="製薬企業名" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="manufacturerName"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.manufacturerName}
								helperText={errors.manufacturerName ? errors.manufacturerName.message : ''}
							/>
						)}
					/>
				</Section>

				<Section label="製薬企業名(英記)" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="manufacturerNameEng"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.manufacturerNameEng}
								helperText={errors.manufacturerNameEng ? errors.manufacturerNameEng.message : ''}
							/>
						)}
					/>
				</Section>

        <Section label="クライアント" labelProps={{ py: 2 }}>
					{clientListState.loading ? <Loading/> : <>
						<Controller
							control={control}
							name="client"
							render={({field}) => (
								<>
									<OptionSelect
										{...field}
										variant="outlined"
										size="small"
										optionList={(clientList ?? []).map((client) => ({
											id: client.clientId,
											label: client.clientName
										}))}
									/>
									{errors.client && (
										<FormHelperText error>{errors.client.message}</FormHelperText>
									)}
								</>
							)}
						/>
					</>}
        </Section>

				<Section label="備考" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="description"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								multiline
								rows={10}
								error={!!errors.description}
								helperText={errors.description ? errors.description.message : ''}
							/>
						)}
					/>
				</Section>
			</Stack>
		</Stack>
	)
}